"use client";

import { useTradeHistoryStore } from "@/vybe-trader-client/trade-history/stores/useTradeHistoryStore";
import { useState, useMemo } from "react";
import { FaChevronLeft } from "react-icons/fa";
import {
  SearchIcon,
  CheckIcon,
  XIcon,
  TrendingUpIcon,
  TrendingDownIcon,
} from "lucide-react";
import { Input } from "@/shadcn-components/ui/input";
import { Button } from "@/shadcn-components/ui/button";
import { Card, CardContent } from "@/shadcn-components/ui/card";
import { Badge } from "@/shadcn-components/ui/badge";
import { Separator } from "@/shadcn-components/ui/separator";

interface SymbolStats {
  symbol: string;
  totalProfit: number;
  tradeCount: number;
}

interface HistorySymbolSelectionScreenProps {
  onBack: () => void;
  onSelectSymbol: (symbol: string) => void;
  selectedSymbol?: string;
}

export default function HistorySymbolSelectionScreen({
  onBack,
  onSelectSymbol,
  selectedSymbol = "allsymbols",
}: HistorySymbolSelectionScreenProps) {
  const [searchQuery, setSearchQuery] = useState("");

  // Pobierz wszystkie dane z store
  const allPositions = useTradeHistoryStore((state) => state.allPositions);
  const isLoading = useTradeHistoryStore((state) => state.isLoading());

  // Stwórz rozszerzoną funkcję getUniqueSymbols z statystykami
  const getUniqueSymbolsWithStats = useMemo((): SymbolStats[] => {
    const symbolMap = new Map<
      string,
      { totalProfit: number; tradeCount: number }
    >();

    // Przejdź przez wszystkie pozycje i policz statystyki
    allPositions.forEach((position) => {
      const symbol = position.symbol;
      if (!symbol || symbol.trim() === "") return;

      const totalProfit =
        position.profit + position.swap + position.commission + position.fee;

      if (symbolMap.has(symbol)) {
        const existing = symbolMap.get(symbol)!;
        existing.totalProfit += totalProfit;
        existing.tradeCount += 1;
      } else {
        symbolMap.set(symbol, {
          totalProfit: totalProfit,
          tradeCount: 1,
        });
      }
    });

    // Konwertuj mapę na tablicę i posortuj alfabetycznie
    return Array.from(symbolMap.entries())
      .map(([symbol, stats]) => ({
        symbol,
        totalProfit: stats.totalProfit,
        tradeCount: stats.tradeCount,
      }))
      .sort((a, b) => a.symbol.localeCompare(b.symbol));
  }, [allPositions]);

  // Filter symbols based on search query
  const filteredSymbols = useMemo(() => {
    if (!searchQuery) return getUniqueSymbolsWithStats;

    return getUniqueSymbolsWithStats.filter((symbolStats) =>
      symbolStats.symbol.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [getUniqueSymbolsWithStats, searchQuery]);

  // Calculate totals for "All symbols"
  const allSymbolsStats = useMemo(() => {
    return getUniqueSymbolsWithStats.reduce(
      (acc, symbolStats) => ({
        totalProfit: acc.totalProfit + symbolStats.totalProfit,
        tradeCount: acc.tradeCount + symbolStats.tradeCount,
      }),
      { totalProfit: 0, tradeCount: 0 }
    );
  }, [getUniqueSymbolsWithStats]);

  const handleSymbolSelect = (symbol: string) => {
    // Konwertuj "All symbols" na "allsymbols" dla store
    const symbolValue = symbol === "All symbols" ? "allsymbols" : symbol;
    onSelectSymbol(symbolValue);
    onBack();
  };

  const formatProfit = (profit: number): string => {
    return profit >= 0
      ? `+$${profit.toFixed(2)}`
      : `-$${Math.abs(profit).toFixed(2)}`;
  };

  const getProfitColor = (profit: number): string => {
    if (profit > 0) return "text-green-400";
    if (profit < 0) return "text-red-400";
    return "text-muted-foreground";
  };

  const getProfitIcon = (profit: number) => {
    if (profit > 0) return <TrendingUpIcon className="h-4 w-4" />;
    if (profit < 0) return <TrendingDownIcon className="h-4 w-4" />;
    return null;
  };

  return (
    <div className="h-full bg-background text-foreground flex flex-col">
      {/* Header with fixed height according to guidelines */}
      <div className="flex items-center justify-between px-4 py-3 h-14 bg-background border-b border-border">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="h-10 w-10"
        >
          <FaChevronLeft className="h-4 w-4 text-blue-400" />
        </Button>
        <h1 className="text-xl font-medium text-foreground">Select Symbol</h1>
        {/* Empty div for spacing balance */}
        <div className="w-10"></div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Search Bar using shadcn Input */}
        <div className="px-4 py-4">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search symbol..."
              className="pl-10 pr-10 h-11"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchQuery("")}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 hover:bg-muted"
              >
                <XIcon className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Symbols List using Card */}
        <div className="px-4 pb-4">
          <Card>
            <CardContent className="p-0">
              {/* All symbols option - always visible */}
              {(!searchQuery ||
                "all symbols".includes(searchQuery.toLowerCase())) && (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => handleSymbolSelect("All symbols")}
                    className="w-full h-auto p-3 justify-between hover:bg-muted/50 rounded-none rounded-t-lg"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-base font-medium">All symbols</span>
                      <span className="text-sm text-muted-foreground">
                        {allSymbolsStats.tradeCount} trades
                      </span>
                      <div
                        className={`flex items-center gap-1 ${getProfitColor(
                          allSymbolsStats.totalProfit
                        )}`}
                      >
                        {getProfitIcon(allSymbolsStats.totalProfit)}
                        <span className="text-sm font-medium">
                          {formatProfit(allSymbolsStats.totalProfit)}
                        </span>
                      </div>
                    </div>
                    {selectedSymbol === "allsymbols" && (
                      <CheckIcon className="h-5 w-5 text-blue-400 flex-shrink-0" />
                    )}
                  </Button>

                  {filteredSymbols.length > 0 && <Separator />}
                </>
              )}

              {/* Loading state */}
              {isLoading ? (
                <div className="py-8 text-center text-muted-foreground">
                  <div className="animate-pulse">Loading symbols...</div>
                </div>
              ) : filteredSymbols.length === 0 && searchQuery ? (
                <div className="py-8 text-center text-muted-foreground">
                  <div className="flex flex-col items-center gap-2">
                    <SearchIcon className="h-8 w-8 text-muted-foreground/50" />
                    <span>No symbols found</span>
                  </div>
                </div>
              ) : (
                /* Filtered symbols with stats */
                filteredSymbols.map((symbolStats, index) => (
                  <div key={symbolStats.symbol}>
                    <Button
                      variant="ghost"
                      onClick={() => handleSymbolSelect(symbolStats.symbol)}
                      className={`w-full h-auto p-3 justify-between hover:bg-muted/50 rounded-none
                        ${
                          index === filteredSymbols.length - 1
                            ? "rounded-b-lg"
                            : ""
                        }
                      `}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-base font-medium">
                          {symbolStats.symbol}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {symbolStats.tradeCount} trades
                        </span>
                        <div
                          className={`flex items-center gap-1 ${getProfitColor(
                            symbolStats.totalProfit
                          )}`}
                        >
                          {getProfitIcon(symbolStats.totalProfit)}
                          <span className="text-sm font-medium">
                            {formatProfit(symbolStats.totalProfit)}
                          </span>
                        </div>
                      </div>
                      {selectedSymbol === symbolStats.symbol && (
                        <CheckIcon className="h-5 w-5 text-blue-400 flex-shrink-0" />
                      )}
                    </Button>
                    {index < filteredSymbols.length - 1 && <Separator />}
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Results count */}
        {searchQuery && (
          <div className="px-4 pb-4">
            <Card>
              <CardContent className="p-3">
                <p className="text-sm text-muted-foreground text-center">
                  {filteredSymbols.length +
                    ("all symbols".includes(searchQuery.toLowerCase())
                      ? 1
                      : 0)}{" "}
                  result
                  {filteredSymbols.length +
                    ("all symbols".includes(searchQuery.toLowerCase())
                      ? 1
                      : 0) !==
                  1
                    ? "s"
                    : ""}{" "}
                  found
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Bottom spacing */}
        <div className="h-6" />
      </div>
    </div>
  );
}
